import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { getProfile } from '../api/rttsClient';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { subscribeStudioOnboardingCompleted } from '../hooks/studioOnboardingSession';

/**
 * Guards authed studio routes: if linked and not onboarded, send to /studio/onboarding.
 * Never wraps /studio/onboarding itself (avoids redirect loops).
 *
 * Session-stable: do not refetch or blank the outlet on pathname changes.
 * Re-run when auth identity changes (Twitch connect / user switch). Completing
 * onboarding notifies the gate so a cached needs_onboarding cannot trap the user.
 */
const StudioOnboardingGate: React.FC = () => {
  const location = useLocation();
  const { status, isLoggedOut, user } = useStudioAuth();
  const [gate, setGate] = useState<'loading' | 'ready' | 'needs_onboarding'>('loading');
  const sessionKey = isLoggedOut
    ? 'logged_out'
    : status === 'linked'
      ? `linked:${user?.uid ?? ''}`
      : status;

  useEffect(() => {
    let cancelled = false;
    if (isLoggedOut || status === 'loading' || status === 'stale_link') {
      setGate('ready');
      return;
    }
    if (status !== 'linked') {
      setGate('ready');
      return;
    }
    setGate('loading');
    void getProfile()
      .then((profile) => {
        if (cancelled) return;
        setGate(profile.onboarded ? 'ready' : 'needs_onboarding');
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open — don't block the studio on profile read errors.
        setGate('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [sessionKey, status, isLoggedOut]);

  useEffect(() => {
    return subscribeStudioOnboardingCompleted(() => {
      setGate('ready');
    });
  }, []);

  if (status === 'loading' || gate === 'loading') {
    return (
      <div className="flex justify-center py-16" data-testid="studio-onboarding-gate-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (gate === 'needs_onboarding') {
    return <Navigate to="/studio/onboarding" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
};

export default StudioOnboardingGate;
